import React from "react";
import { rest } from "../../data/rest/api";
import { startGlobalLoading, stopGlobalLoading } from "../../layout/globalLoading";
import { toast } from "react-toastify";
import { showModal, closeModal } from "../../layout/modal";

const POLL_INTERVAL = 5000;

const ConfirmModal2 = ({ instanceName, selectedBackup, onConfirm }) => {
	const [confirmText, setConfirmText] = React.useState("");

	return (
		<div>
			<p style={{ color: "red" }}>
				<b>Zadnja provjera.</b> Ova akcija je nepovratna.
			</p>
			<p>
				Upiši naziv instance <b>{instanceName}</b> da potvrdiš restore:
			</p>
			<input
				type="text"
				className="form-control mb-3"
				value={confirmText}
				onChange={(e) => setConfirmText(e.target.value)}
				placeholder={instanceName}
				autoFocus
			/>
			<p className="text-muted" style={{ fontSize: "0.85rem" }}>
				Backup: <code>{selectedBackup}</code>
			</p>
			<button onClick={closeModal} className="btn btn-secondary mr-2">
				ODUSTANI
			</button>
			<button
				disabled={confirmText !== instanceName}
				onClick={() => {
					closeModal();
					onConfirm();
				}}
				className="btn btn-danger"
				style={{ marginLeft: "10px" }}
			>
				POKRENI RESTORE
			</button>
		</div>
	);
};

const ConfirmModal1 = ({ instanceName, selectedBackup, onContinue }) => {
	return (
		<div>
			<h4 style={{ color: "red" }}>Upozorenje!</h4>
			<p>
				Ova akcija će <b>trajno prepisati</b> cijelu bazu instance{" "}
				<b>{instanceName}</b>.
			</p>
			<p>
				<b>Trenutni podaci bit će nepovratno izgubljeni.</b>
			</p>
			<p>
				Backup koji će biti restoran:
				<br />
				<code style={{ wordBreak: "break-all" }}>{selectedBackup}</code>
			</p>
			<button onClick={closeModal} className="btn btn-secondary mr-2">
				ODUSTANI
			</button>
			<button
				onClick={onContinue}
				className="btn btn-danger"
				style={{ marginLeft: "10px" }}
			>
				RAZUMIJEM, NASTAVI
			</button>
		</div>
	);
};

const DbRestore = (props) => {
	const [instances, setInstances] = React.useState([]);
	const [selectedInstance, setSelectedInstance] = React.useState("");
	const [backups, setBackups] = React.useState([]);
	const [selectedBackup, setSelectedBackup] = React.useState("");
	const [output, setOutput] = React.useState("");
	const [restoring, setRestoring] = React.useState(false);
	const pollRef = React.useRef(null);

	React.useEffect(() => {
		rest("GET", "/api/instance")
			.then((data) => {
				const list = Array.isArray(data) ? data : data.rows || [];
				setInstances(list);
			})
			.catch((err) => {
				toast.error("Greška pri dohvatu instanci: " + err.message);
			});

		// Pri mountu provjeri je li neki restore već u tijeku
		rest("GET", "/api/dbRestoreActive").then((active) => {
			if (active.running) {
				setSelectedInstance(active.instanceName);
				setRestoring(true);
				setOutput(`Restore je već u tijeku za instancu "${active.instanceName}"...\n\nBackup: ${active.gcsPath}`);
				toast.info("Restore je u tijeku, nastavljam praćenje...");
				startPolling(active.instanceName);
			}
		}).catch(() => {});

		return () => {
			if (pollRef.current) clearInterval(pollRef.current);
		};
	}, []);

	const startPolling = (instanceName) => {
		if (pollRef.current) clearInterval(pollRef.current);

		pollRef.current = setInterval(async () => {
			try {
				const status = await rest("GET", `/api/dbRestoreStatus/${instanceName}`);

				if (status.status === "done") {
					clearInterval(pollRef.current);
					setRestoring(false);
					setOutput(`Restore završen.\n\nBaza: hotel_${instanceName}\nBackup: ${status.gcsPath}\nVrijeme: ${status.finishedAt}`);
					toast.success("Restore je uspješno završen!");
				} else if (status.status === "error") {
					clearInterval(pollRef.current);
					setRestoring(false);
					setOutput(`Restore je failao.\n\nGreška: ${status.error}`);
					toast.error("Restore je failao: " + status.error);
				}
			} catch (err) {
				clearInterval(pollRef.current);
				setRestoring(false);
			}
		}, POLL_INTERVAL);
	};

	const loadBackups = async () => {
		if (!selectedInstance) {
			alert("Odaberi instancu.");
			return;
		}
		try {
			startGlobalLoading();
			setBackups([]);
			setSelectedBackup("");
			setOutput("");
			const result = await rest("POST", "/api/dbRestoreListBackups", {
				instanceName: selectedInstance,
			});
			if (result.backups.length === 0) {
				toast.error(`Nema dostupnih backupa za instancu "${selectedInstance}".`);
			} else {
				setBackups(result.backups);
				setSelectedBackup(result.backups[0]);
				toast.success(`Pronađeno ${result.backups.length} backupa.`);
			}
		} catch (err) {
			toast.error(err.message);
		} finally {
			stopGlobalLoading();
		}
	};

	const executeRestore = async () => {
		try {
			startGlobalLoading();
			const result = await rest("POST", "/api/dbRestore", {
				instanceName: selectedInstance,
				gcsPath: selectedBackup,
			});
			setRestoring(true);
			setOutput(`Restore pokrenut...\n\nBaza: ${result.database}\nBackup: ${result.gcsPath}`);
			toast.info("Restore je pokrenut. Polling svakih 5s...");
			startPolling(selectedInstance);
		} catch (err) {
			toast.error(err.message);
		} finally {
			stopGlobalLoading();
		}
	};

	const openWarning2 = () => {
		showModal(
			"Potvrda restore operacije",
			<ConfirmModal2
				instanceName={selectedInstance}
				selectedBackup={selectedBackup}
				onConfirm={executeRestore}
			/>,
			false
		);
	};

	const openWarning1 = () => {
		if (!selectedInstance || !selectedBackup) {
			alert("Odaberi instancu i backup.");
			return;
		}
		showModal(
			"Upozorenje — nepovratna operacija",
			<ConfirmModal1
				instanceName={selectedInstance}
				selectedBackup={selectedBackup}
				onContinue={openWarning2}
			/>,
			false
		);
	};

	const formatBackupLabel = (gcsPath) => {
		const parts = gcsPath.split("/");
		return parts[parts.length - 1];
	};

	return (
		<div className="container">
			<div className="jumbotron jumbotron-fluid">
				<div>
					<h1 className="display-4">DB Restore</h1>
					<p className="lead">
						Restore odabranog backupa na instancu.{" "}
						<b style={{ color: "red" }}>Nepovratna operacija.</b>
					</p>
				</div>
			</div>

			<div className="w-50">
				<div className="form-group mt-2">
					<label>Instanca</label>
					<select
						className="form-control"
						value={selectedInstance}
						disabled={restoring}
						onChange={(e) => {
							setSelectedInstance(e.target.value);
							setBackups([]);
							setSelectedBackup("");
							setOutput("");
						}}
					>
						<option value="">-- odaberi instancu --</option>
						{instances.map((inst) => (
							<option key={inst.uuid} value={inst.name}>
								{inst.name}
							</option>
						))}
					</select>
				</div>

				<button onClick={loadBackups} className="btn btn-secondary mb-4" disabled={restoring}>
					UČITAJ BACKUPE
				</button>

				{backups.length > 0 && (
					<div className="form-group">
						<label>Dostupni backupi</label>
						<select
							className="form-control"
							value={selectedBackup}
							disabled={restoring}
							onChange={(e) => setSelectedBackup(e.target.value)}
						>
							{backups.map((b) => (
								<option key={b} value={b}>
									{formatBackupLabel(b)}
								</option>
							))}
						</select>
					</div>
				)}
			</div>

			{backups.length > 0 && (
				<>
					<button
						onClick={openWarning1}
						className="btn btn-danger mt-3 mb-3"
						disabled={restoring}
					>
						{restoring ? "RESTORE U TIJEKU..." : "RESTORE"}
					</button>
					&ensp;
					<button
						onClick={() => props.history.goBack()}
						className="btn btn-outline-secondary mt-3 mb-3"
						disabled={restoring}
					>
						CANCEL
					</button>
				</>
			)}

			{!backups.length && (
				<button
					onClick={() => props.history.goBack()}
					className="btn btn-outline-secondary mt-3 mb-3"
					disabled={restoring}
				>
					CANCEL
				</button>
			)}

			{output && (
				<textarea
					readOnly={true}
					style={{ width: "100%", height: "12vh" }}
					value={output}
					className="mt-3"
				/>
			)}
		</div>
	);
};

export default DbRestore;
